import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { endOfDay, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import AudioPlayerModal from '../../../components/Modals/AudioPlayerModal';
import {
  convertToMask,
  fileDownload,
  formatArchiveOptions,
  formatDownloadDate,
  formatPayloadDate,
  getDateRangeFromLabel,
  yesNoOptions
} from '../../../utils/common';
import FormDateInput from '../../../components/form/FormDateInput';
import AdminHeader from '../../../components/header/AdminHeader';
import FormSelect from '../../../components/form/FormSelect';
import Pagination from '../../../components/form/Pagination';
import CallexLoader from '../../../components/CallexLoader';

import { useUserDetails } from '../../../store/slices/authSlice';
import { auditorReportGet } from '../../../store/slices/tenantSlices/auditorSlice';
import {
  archiveOptionsGet,
  processAllOptionGet,
  userOptionGetByProcess
} from '../../../store/slices/tenantSlices/dailyReportSlice';
import { useNavigate } from 'react-router-dom';
import config from '../../../config';
import { useLoginUserType, useTimezone } from '../../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';

const AuditReport = () => {
  const timezone = useTimezone();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useUserDetails();
  const loginUserType = useLoginUserType();

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [audioDetails, setAudioDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [processOptions, setProcessOptions] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isCsvDownload, setIsCsvDownload] = useState(false);
  const [csvReportData, setCsvReportData] = useState([]);
  const [csvHeaderData, setCsvHeaderData] = useState([]);
  const [paginationDetails, setPaginationDetails] = useState({ page: 1, total: 0, limit: 10 });
  const [isApiCall, setIsApiCall] = useState(false);
  const [archiveOptions, setArchiveOptions] = useState([]);
  const [headerFields, setHeaderFields] = useState({
    default: [],
    crm_fields: []
  });

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    page: 1,
    limit: 10,
    processIds: [],
    user_id: '',
    is_audit: 'yes',
    from_date: startOfDay(today),
    to_date: endOfDay(today),
    archiveId: 0
  };

  const validationSchema = Yup.object({
    from_date: Yup.date().required('Start date is required'),
    to_date: Yup.date().required('End date is required'),
    processIds: Yup.array().min(1, 'Process is required').required('Process is required')
    // user_id: Yup.string().required('User is required')
  });

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (loginUserType === 'auditor') {
      const options = user?.processIds.map((val) => ({
        label: val?.name,
        value: val?.id
      }));
      setProcessOptions(options);
    } else {
      getProcessOption();
    }
  }, [user?.processIds, loginUserType]);

  const onSubmit = async () => {
    setIsLoading(true);
    setIsFilterOpen(false);
    localStorage.setItem('last-searched-report', JSON.stringify(values));
    try {
      const formattedStartDate = formatPayloadDate(values.from_date);
      const formattedEndDate = formatPayloadDate(values.to_date);
      const serializedProcessId = `[${[values?.processIds].join(',')}]`;
      const payload = {
        ...values
      };
      payload['from_date'] = formattedStartDate;
      payload['to_date'] = formattedEndDate;
      payload['processIds'] = serializedProcessId;
      payload['user_id'] = Number(values?.user_id);
      const response = await dispatch(auditorReportGet(payload)).unwrap();
      if (response) {
        setHeaderFields({
          ...headerFields,
          default: response?.header?.default || [],
          crm_fields: response?.header?.crm_fields || []
        });
        setReportData(response?.data || []);
        setPaginationDetails({
          ...paginationDetails,
          limit: response.limit,
          page: response.page,
          total: response.total
        });
      }
      setIsLoading(false);
      setIsApiCall(false);
    } catch (error) {
      setIsLoading(false);
      setIsApiCall(false);
      console.log('error', error);
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    handleSubmit,
    touched,
    errors,
    setValues
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  useEffect(() => {
    if (values?.processIds.length > 0 || isApiCall) {
      onSubmit();
    }
  }, [values?.limit, values?.page, isApiCall]);

  useEffect(() => {
    const data = localStorage.getItem('last-searched-report');
    if (data) {
      const val = JSON.parse(data);
      setValues({
        ...val
      });
      setIsApiCall(true);
    }
  }, []);

  const getUserOption = async () => {
    try {
      const serializedProcessId = `[${[values?.processIds].join(',')}]`;
      const response = await dispatch(
        userOptionGetByProcess({ processIds: serializedProcessId })
      ).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.Username || '-',
          value: val.id
        }));
        setUserOptions(options);
      } else {
        setUserOptions([]);
      }
    } catch (error) {
      setUserOptions([]);
      console.log('error', error);
    }
  };
  const getArchiveOptions = async () => {
    try {
      const response = await dispatch(archiveOptionsGet()).unwrap();
      if (response && response.data) {
        const options = formatArchiveOptions(response.data, timezone);
        setArchiveOptions(options);
      } else {
        setArchiveOptions([]);
      }
    } catch (error) {
      setArchiveOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getArchiveOptions();
  }, []);

  useEffect(() => {
    if (values?.processIds?.length > 0) {
      getUserOption();
    } else {
      setUserOptions([]);
    }
  }, [values?.processIds]);

  const selectedOption = useMemo(
    () => archiveOptions.find((opt) => opt.value === values.archiveId),
    [values.archiveId, archiveOptions]
  );

  const selectedLabel = selectedOption?.label || null;

  const { minDate, maxDate } = useMemo(() => {
    return getDateRangeFromLabel(selectedLabel, timezone);
  }, [selectedLabel, timezone]);

  useEffect(() => {
    if (minDate && maxDate) {
      setFieldValue('from_date', minDate);
      setFieldValue('to_date', maxDate);
    }
  }, [minDate?.getTime(), maxDate?.getTime()]);

  const prepareExportProps = () => {
    const data = csvReportData || [];
    return {
      headers: csvHeaderData,
      data,
      filename: `Audit_report_${formatDownloadDate(values.from_date)}.csv`
    };
  };

  const handleExportCSV = async () => {
    setIsExporting(true);

    try {
      const formattedStartDate = formatPayloadDate(values.from_date);
      const formattedEndDate = formatPayloadDate(values.to_date);
      const serializedProcessId = `[${[values?.processIds].join(',')}]`;
      const payload = {
        ...values
      };
      payload['from_date'] = formattedStartDate;
      payload['to_date'] = formattedEndDate;
      payload['processIds'] = serializedProcessId;
      payload['user_id'] = Number(values?.user_id);

      delete payload['limit'];
      delete payload['page'];

      const response = await dispatch(auditorReportGet(payload)).unwrap();

      if (response) {
        const combinedHeaders = [
          ...(response?.header?.default || []),
          ...(response?.header?.crm_fields || [])
        ];

        const headers = combinedHeaders.map((val) => ({ label: val?.label, key: val?.name }));

        setCsvReportData(response?.data || []);
        setCsvHeaderData(headers);
        setIsExporting(false);
        setIsCsvDownload(true);
      }
    } catch (error) {
      setIsExporting(false);
      console.log('Error exporting CSV:', error);
    }
  };

  return (
    <div>
      <AdminHeader
        title="Audit Report"
        isSearch={true}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isExport={reportData?.length > 0}
        csvProps={prepareExportProps()}
        onExportClick={() => handleExportCSV()}
        setIsCsvDownload={setIsCsvDownload}
        isCsvDownload={isCsvDownload}
        isExporting={isExporting}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Month"
                  placeholder="Select month"
                  value={values.archiveId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="archiveId"
                  options={archiveOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormDateInput
                  name="from_date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  value={values.from_date}
                  minDate={minDate}
                  maxDate={values.to_date || maxDate}
                  onChange={(e) => setFieldValue('from_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormDateInput
                  label="To Date"
                  name="to_date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  minDate={values.from_date || minDate}
                  maxDate={maxDate}
                  value={values.to_date}
                  onChange={(e) => setFieldValue('to_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Process"
                  placeholder="Select Process"
                  value={values.processIds}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="processIds"
                  options={processOptions}
                  touched={touched}
                  errors={errors}
                  isMulti={true} // Enable multi-select
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="User"
                  placeholder="Select User"
                  value={values.user_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="user_id"
                  options={[{ label: 'All', value: '' }, ...(userOptions || [])]}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Is Audit"
                  placeholder="Select Is Audit"
                  value={values.is_audit}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="is_audit"
                  options={yesNoOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.length > 0 ? (
        <Card className="p-0 position-relative">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 369px)' : 'calc(100vh - 220px)' }}>
            <table className="report-list-table">
              <thead>
                <tr className="title">
                  <th colSpan={headerFields?.default?.length + 1}>Default</th>
                </tr>
                <tr>
                  {headerFields?.default?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {reportData?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {headerFields?.default?.map((value, idx) => {
                        if (value?.name === 'recording_file') {
                          return (
                            <td key={idx} className="text-center cursor-pointer">
                              {val[value?.name] ? (
                                <div className="d-flex gap-2">
                                  <Icon.PlayCircle
                                    size={16}
                                    onClick={() =>
                                      setAudioDetails({
                                        ...val,
                                        src: `${config.BASE_URL}cdrs_report/download?filename=${val.recording_file}`
                                      })
                                    }
                                    className="cursor-pointer text-info"
                                  />
                                  <Icon.Download
                                    size={16}
                                    onClick={() => {
                                      fileDownload(
                                        `${config.BASE_URL}cdrs_report/download?filename=${val.recording_file}`
                                      );
                                    }}
                                  />
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                          );
                        }
                        if (value?.name === 'crm_number') {
                          return (
                            <td key={idx}>
                              {process.env.REACT_APP_GLOBAL_NUMBER_MASKING === 'true'
                                ? convertToMask(val[value?.name] || '-')
                                : val[value?.name] || '-'}
                            </td>
                          );
                        }
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                      <td>
                        <div className="text-center">
                          <Icon.FileText
                            size={16}
                            className="cursor-pointer text-blue"
                            onClick={() =>
                              navigate(
                                `/reports/${loginUserType === 'auditor' ? 'audit-report' : 'auditor-report'}/${val?.call_history_id}/${values?.archiveId}`
                              )
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <table className="report-list-table">
              <thead>
                <tr className="title">
                  <th colSpan={headerFields?.crm_fields?.length}>CRM</th>
                </tr>
                <tr>
                  {headerFields?.crm_fields?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {reportData?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {headerFields?.crm_fields?.map((value, idx) => {
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-65px' }}>
            <Pagination
              totalCount={paginationDetails?.total}
              limit={paginationDetails?.limit}
              page={paginationDetails?.page}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
              }}
              onPageClick={(val) => setFieldValue('page', val)}
              lastPageClick={(val) => setFieldValue('page', val)}
              firstPageClick={(val) => setFieldValue('page', val)}
              onRefreshClick={() => onSubmit()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      {audioDetails && (
        <AudioPlayerModal
          modalOpen={!!audioDetails}
          handleClose={() => setAudioDetails(null)}
          details={audioDetails}
        />
      )}
    </div>
  );
};

export default AuditReport;
